import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Share2, Link2, Palette, ArrowRight } from 'lucide-react';
import { applySeo } from '../../utils/seo';
import { ogBrandImageUrl } from '../../constants/ogBrand';
import { normalizeUiLang } from '../../utils/localePreference';
import { digitalCardStyles } from './digitalCardStyles';
import DigitalCardNav from './DigitalCardNav';
import DigitalCardFooter from './DigitalCardFooter';
import PreviewPhoneFrame from './PreviewPhoneFrame';
import TcardsShowcaseSection from './TcardsShowcaseSection';
import { SHOWCASE_BUSINESS_PREVIEW } from './templatePreviewAssets';
import { TEMPLATES_CHOOSE_URL } from './digitalCardRoutes';

const DigitalCardLanding: React.FC = () => {
  const { t, i18n } = useTranslation();
  const resolvedLang = normalizeUiLang(i18n.resolvedLanguage || i18n.language);
  const [plans, setPlans] = useState<any[]>([]);

  useEffect(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.tcards.org';
    const dispose = applySeo({
      title: t('dcl_seo_title', { defaultValue: 'TCards Digital Business Card | Your Professional Digital Identity' }),
      description: t('dcl_seo_desc', {
        defaultValue:
          'Create a professional TCards Digital Business Card with your bio, contact information, social profiles, business links and more. Choose a template, personalize your TCard and share it anywhere.',
      }),
      keywords: ['Digital Business Card', 'Digital Card', 'Online Business Card', 'Professional Digital Card', 'Virtual Business Card', 'Digital Profile'],
      image: ogBrandImageUrl(origin, 'site'),
      url: `${origin}/digital-card`,
      canonicalUrl: `${origin}/digital-card`,
      siteName: 'TCards Digital Business Card',
    });
    return dispose;
  }, [t, resolvedLang]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/public/subscription-plans');
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length) setPlans(data.slice(0, 3));
        }
      } catch {}
    })();
  }, []);

  const pillars = [
    {
      icon: Link2,
      titleKey: 'dcl_pillar_connect_title',
      descKey: 'dcl_pillar_connect_desc',
      titleDefault: 'Connect everything you share',
      descDefault: 'Profile, bio, contacts, social profiles and links — all in one polished digital card.',
    },
    {
      icon: Share2,
      titleKey: 'dcl_pillar_share_title',
      descKey: 'dcl_pillar_share_desc',
      titleDefault: 'Share anywhere instantly',
      descDefault: 'Send your TCard via WhatsApp, email, LinkedIn, QR code or any app you already use.',
    },
    {
      icon: Palette,
      titleKey: 'dcl_pillar_design_title',
      descKey: 'dcl_pillar_design_desc',
      titleDefault: 'Designs that feel like you',
      descDefault: 'Pick a Business or Artist template, personalize it in minutes, and make a memorable first impression.',
    },
  ];

  const steps = [
    { n: 1, titleKey: 'dcl_step1_title', descKey: 'dcl_step1_desc' },
    { n: 2, titleKey: 'dcl_step2_title', descKey: 'dcl_step2_desc' },
    { n: 3, titleKey: 'dcl_step3_title', descKey: 'dcl_step3_desc' },
    { n: 4, titleKey: 'dcl_step4_title', descKey: 'dcl_step4_desc' },
  ];

  const platforms = ['LinkedIn', 'Instagram', 'WhatsApp', 'Website', 'Facebook', 'TikTok', 'YouTube', 'X'];

  return (
    <div className="dcl-root dcl-root--linktree">
      <style>{digitalCardStyles}</style>
      <DigitalCardNav activePath="/digital-card" />

      {/* Hero — Linktree-style bold block */}
      <section className="dcl-hero-lt">
        <div className="dcl-container dcl-hero-lt-inner">
          <div className="dcl-hero-lt-copy">
            <h1 className="dcl-hero-lt-title">
              {t('dcl_hero_title', { defaultValue: 'Your Digital Identity, Built Around You.' })}
            </h1>
            <p className="dcl-hero-lt-sub">
              {t('dcl_hero_sub', {
                defaultValue:
                  'Create one professional TCard to introduce yourself, share your contacts, social profiles and business links — all from one beautifully designed digital card.',
              })}
            </p>
            <div className="dcl-hero-lt-actions">
              <Link to={TEMPLATES_CHOOSE_URL} className="dcl-btn dcl-btn--dark dcl-btn--pill">
                {t('dcl_nav_get_started', { defaultValue: 'Get Started' })}
              </Link>
            </div>
            <p className="dcl-signin-link dcl-signin-link--hero">
              {t('dcl_already_account', { defaultValue: 'Already have an account?' })}{' '}
              <Link to="/auth?login=1">{t('dcl_nav_sign_in', { defaultValue: 'Sign In' })}</Link>
            </p>
          </div>
          <div className="dcl-hero-lt-visual">
            <PreviewPhoneFrame
              src={SHOWCASE_BUSINESS_PREVIEW}
              alt={t('dcl_showcase_business_alt', { defaultValue: 'TCards Business digital card example' })}
              size="lg"
              className="dcl-hero-lt-phone"
            />
          </div>
        </div>
      </section>

      {/* Dual-phone showcase */}
      <TcardsShowcaseSection />

      {/* Three pillars */}
      <section className="dcl-section dcl-section--white">
        <div className="dcl-container">
          <h2 className="dcl-section-title dcl-section-title--center">
            {t('dcl_pillars_heading', { defaultValue: 'The digital card built for how you connect.' })}
          </h2>
          <div className="dcl-pillar-grid">
            {pillars.map(({ icon: Icon, titleKey, descKey, titleDefault, descDefault }) => (
              <article key={titleKey} className="dcl-pillar-card">
                <div className="dcl-pillar-icon"><Icon size={22} strokeWidth={2.2} /></div>
                <h3 className="dcl-pillar-title">{t(titleKey, { defaultValue: titleDefault })}</h3>
                <p className="dcl-pillar-desc">{t(descKey, { defaultValue: descDefault })}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms — lime band */}
      <section className="dcl-section dcl-section--lime">
        <div className="dcl-container dcl-container--narrow" style={{ textAlign: 'center' }}>
          <h2 className="dcl-section-title dcl-section-title--center dcl-section-title--forest">
            {t('dcl_presence_title', { defaultValue: 'Bring your digital presence together' })}
          </h2>
          <p className="dcl-section-sub dcl-section-sub--center dcl-section-sub--forest">
            {t('dcl_presence_sub', {
              defaultValue:
                'Your professional information is often spread across several platforms. TCards brings the important parts together into one identity.',
            })}
          </p>
          <div className="dcl-chip-row">
            {platforms.map((p) => (
              <span key={p} className="dcl-chip dcl-chip--forest">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="dcl-section dcl-section--white">
        <div className="dcl-container">
          <h2 className="dcl-section-title dcl-section-title--center">
            {t('dcl_how_title', { defaultValue: 'Create yours in a few simple steps.' })}
          </h2>
          <div className="dcl-steps-lt">
            {steps.map(({ n, titleKey, descKey }) => (
              <div key={n} className="dcl-step-lt">
                <span className="dcl-step-lt-num">{n}</span>
                <h3 className="dcl-step-lt-title">{t(titleKey)}</h3>
                <p className="dcl-step-lt-desc">{t(descKey)}</p>
              </div>
            ))}
          </div>
          <div className="dcl-section-cta-row">
            <Link to={TEMPLATES_CHOOSE_URL} className="dcl-btn dcl-btn--dark dcl-btn--pill">
              {t('dcl_cta_explore_templates', { defaultValue: 'Explore Templates' })}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing */}
      {plans.length > 0 && (
        <section className="dcl-section dcl-section--soft" id="pricing">
          <div className="dcl-container">
            <h2 className="dcl-section-title dcl-section-title--center">
              {t('dcl_pricing_title', { defaultValue: 'Simple, transparent pricing' })}
            </h2>
            <p className="dcl-section-sub dcl-section-sub--center">
              {t('dcl_pricing_sub', {
                defaultValue: 'Choose a plan after you pick your template. Start free, upgrade when you are ready.',
              })}
            </p>
            <div className="dcl-pricing-grid">
              {plans.map((p: any) => (
                <div key={p.id || p.name} className="dcl-pricing-card">
                  <h3 className="dcl-pricing-name">{p.name}</h3>
                  <p className="dcl-pricing-price">{p.price}</p>
                  <p className="dcl-pricing-desc">{p.description}</p>
                  <Link to={TEMPLATES_CHOOSE_URL} className="dcl-btn dcl-btn--dark dcl-btn--sm dcl-btn--pill">
                    {t('dcl_nav_get_started', { defaultValue: 'Get Started' })}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA — Linktree jumpstart block */}
      <section className="dcl-section dcl-final-lt">
        <div className="dcl-container dcl-container--narrow">
          <h2 className="dcl-final-lt-title">
            {t('dcl_final_title', { defaultValue: 'Make your digital introduction count.' })}
          </h2>
          <p className="dcl-final-lt-sub">
            {t('dcl_final_sub', {
              defaultValue:
                'Choose a template, personalize your TCard, and share one link that tells people who you are.',
            })}
          </p>
          <Link to={TEMPLATES_CHOOSE_URL} className="dcl-btn dcl-btn--dark dcl-btn--pill dcl-btn--lg">
            {t('dcl_nav_get_started', { defaultValue: 'Get Started' })}
          </Link>
          <p className="dcl-signin-link dcl-signin-link--final">
            {t('dcl_already_account', { defaultValue: 'Already have an account?' })}{' '}
            <Link to="/auth?login=1">{t('dcl_nav_sign_in', { defaultValue: 'Sign In' })}</Link>
          </p>
        </div>
      </section>

      <DigitalCardFooter />
    </div>
  );
};

export default DigitalCardLanding;
